// Employee mobile landing — DESIGN §6.2.1 (PO Spec §1.5, 2026-06-21).
// Renders below the `md` breakpoint as the "Maeve-First" entry for
// the bereaved employee. The "I just need to talk" card is the
// most prominent (Companion Mode entry) — the spec's priority
// option. The remaining cards deep-link into existing portal
// surfaces.
//
// Spec greeting copy (PO §1.5) — "Hey [First Name], I'm here to
// help you through this." — gated on the JWT `name` claim
// (Phase 0.2) AND on the CGC sign-off per PROJECT §"Mobile-First
// Maeve Entry". The fallback below is neutral, anti-anthropomorphism-safe
// copy (DESIGN §1.4 #8) for a missing name: no first-person plural ("we"),
// no availability promise, no personalised address — see the module
// docstring in `routes/Portal.tsx`.
//
// The card order (Talk → Touch points → Leave & entitlements) is the
// spec's priority: the live conversation surface comes first
// because the employee-facing emotional load is the platform's
// primary support, not the workflow surface.

import type { EmployeePortal as EmployeePortalResponse } from '@besideu/shared/cases';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import { ActionCard } from '@/components/ui/ActionCard.tsx';
import { ProminentActionCard } from '@/components/ui/ProminentActionCard.tsx';

export function EmployeeMaeveFirst({
  data,
  firstName,
  links = {
    talk: '/portal/maeve',
    touchpoints: '/portal/check-ins',
    entitlements: '/portal/leave',
  },
}: {
  data: EmployeePortalResponse;
  firstName: string;
  /**
   * Card destinations. These used to default to same-page anchors
   * (`#talk`/`#touchpoints`/`#entitlements`), which were correct only inside
   * `Portal.tsx`'s old long-stack mobile branch — the one layout that
   * rendered the anchor targets on the same page. That branch is gone: since
   * the portal became one route tree, each destination is a real section
   * route, so the defaults are now the routes and there is no context in
   * which they're dead. The prop stays for a caller that needs to redirect a
   * card elsewhere; nothing overrides it today.
   */
  links?: { talk: string; touchpoints: string; entitlements: string };
}) {
  const nextTouchpoint = data.touchpoints.find((c) => !c.completedAt);

  return (
    <section className="space-y-8">
      <div className="flex flex-col items-center text-center">
        {/* §7 employee entry — role badge "Your space" in blue at 45%. */}
        <p className="text-xs font-semibold tracking-[2px] text-employee-blue/45 uppercase">
          Your space
        </p>
        <MaeveAvatar size={140} variant="warm" className="mt-4" />
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground">
          {firstName
            ? `Hey ${firstName}, I'm here to help you through this.`
            : 'Support and next steps.'}
        </h1>
        {/* §7 — italic sub-line under the employee greeting. The spec copy
            ("What can I do for you today?") is first-person Maeve voice, so —
            like the personalised greeting above — it is gated on the same
            CGC sign-off proxy (`firstName`). Until sign-off, the neutral,
            anti-anthropomorphism-safe fallback (§1.4 #8) renders. */}
        <p className="mt-2 text-base text-muted-foreground italic">
          {firstName ? 'What can I do for you today?' : 'Choose where to start.'}
        </p>
      </div>

      {/* PO Spec §1.5 — "I just need to talk" is the most prominent
          card on the Employee Maeve-First page. The card uses a
          larger `ProminentActionCard` variant so it stands out
          from the secondary touch point / entitlements cards below.
          The deep-link scrolls the user to the existing
          `EmployeeCompanionChat` surface rendered in the dashboard
          below (matches the anchor pattern the prior
          `EmployeeMaeveFirst` used). */}
      <div className="space-y-3">
        <ProminentActionCard
          to={links.talk}
          title="I just need to talk"
          subtitle="A private, quiet space to write at your own pace."
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" focusable={false}>
              <path
                d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <ActionCard
          to={links.touchpoints}
          title="Your touch points"
          subtitle={
            nextTouchpoint
              ? `Next: ${labelForTouchpointType(nextTouchpoint.type)} on ${nextTouchpoint.scheduledFor}.`
              : 'No touch points scheduled yet.'
          }
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" focusable={false}>
              <rect
                x="3.5"
                y="5"
                width="17"
                height="15"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M3.5 9.5h17M8 3.5v3M16 3.5v3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          }
        />
        <ActionCard
          to={links.entitlements}
          title="Leave and entitlements"
          subtitle="What your organisation provides following a bereavement."
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" focusable={false}>
              <path
                d="M4 10.5L12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1V10.5z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
      </div>
    </section>
  );
}

function labelForTouchpointType(
  type: EmployeePortalResponse['touchpoints'][number]['type'],
): string {
  switch (type) {
    case 'Day_3':
      return 'Day 3';
    case 'Week_1':
      return 'Week 1';
    case 'Pre_Return':
      return 'Pre-return';
    case 'Week_1_Back':
      return 'Week 1 back';
    case 'Month_1':
      return 'Month 1';
    case 'Month_3':
      return 'Month 3';
    case 'Month_6':
      return 'Month 6';
    case 'Month_9':
      return 'Month 9';
    case 'Month_12':
      return 'Month 12';
    case 'Anniversary':
      return 'Anniversary';
  }
}
